// Port of Services/SBotTools/SBotToolAuthorizationService.swift — resolves the
// user's league capability for tool calls and enforces role gates. Error
// message strings are verbatim from iOS (the model reads them).

import {
  activeMembership,
  isMembershipCurrentlyActive,
  resolvedLeagueID,
  type SBotMembership,
  type SBotUser,
  type SBotUserRole,
} from "@/models/user";
import type { SBotChatWorkspaceScope } from "@/models/aiChat";
import {
  canManageBroadcastOverlays,
  canReadBroadcastOverlays,
  canUseAI,
} from "@/models/featureAccessPolicy";
import { makeHandle, parseHandle, type ToolJSONValue } from "./toolEnvelope";

export interface SBotToolWorkspaceOptionInfo {
  leagueID?: string | undefined;
  sportID?: string | undefined;
  displayName?: string | undefined;
}

export interface SBotToolExecutionContext {
  currentUser: SBotUser | null;
  workspaceOptions: SBotToolWorkspaceOptionInfo[];
  workspaceScope: SBotChatWorkspaceScope | undefined;
  isSBotLabsAdmin: boolean;
}

export interface SBotToolLeagueCapability {
  leagueID: string;
  leagueName: string;
  sportID?: string | undefined;
  role?: SBotUserRole | undefined;
  isSBotLabsAdmin: boolean;
  canManageSettings: boolean;
  canManageInvitations: boolean;
  canManageAthleteData: boolean;
  canManageCompetitionData: boolean;
  canManageOrganizationAdminData: boolean;
  canUseAI: boolean;
  canReadBroadcastOverlays: boolean;
  canManageBroadcastOverlays: boolean;
}

export class SBotToolAuthorizationError extends Error {
  readonly kind:
    | "notSignedIn"
    | "noActiveLeague"
    | "notLeagueMember"
    | "missingLeagueAdminRole"
    | "missingLeagueManagementRole"
    | "missingInviteRole"
    | "missingBroadcastOverlayReadAccess"
    | "missingBroadcastOverlayManagementRole"
    | "workspaceScopeMismatch";

  constructor(kind: SBotToolAuthorizationError["kind"]) {
    super(AUTH_ERROR_MESSAGES[kind]);
    this.name = "SBotToolAuthorizationError";
    this.kind = kind;
  }
}

const AUTH_ERROR_MESSAGES = {
  notSignedIn: "Sign in before using SBot league tools.",
  noActiveLeague: "Select an active league workspace before using this tool.",
  notLeagueMember: "This league is not available to the current user.",
  missingLeagueAdminRole: "An active league admin role is required for this action.",
  missingLeagueManagementRole: "An active league admin or league manager role is required for this action.",
  missingInviteRole: "This invite target is not available to the current user.",
  missingBroadcastOverlayReadAccess: "AI and Broadcast Overlays access are required in the active workspace.",
  missingBroadcastOverlayManagementRole: "An active league admin, league manager, or SBot Labs admin role with Broadcast Overlays access is required for this action.",
  workspaceScopeMismatch: "This tool call targets a different workspace. Switch workspaces or copy the chat before using that league.",
} as const;

export class SBotToolAuthorizationService {
  private readonly context: SBotToolExecutionContext;

  constructor(context: SBotToolExecutionContext) {
    this.context = context;
  }

  requireUser(): SBotUser {
    const user = this.context.currentUser;
    if (!user) throw new SBotToolAuthorizationError("notSignedIn");
    return user;
  }

  activeLeagueCapability(): SBotToolLeagueCapability {
    const user = this.requireUser();
    const scopeLeagueID = this.context.workspaceScope?.leagueID?.trim();
    if (scopeLeagueID) return this.capabilityForLeague(scopeLeagueID);

    const contextLeagueID = user.activeContext ? resolvedContextLeagueID(user.activeContext) : undefined;
    if (contextLeagueID) return this.capabilityForLeague(contextLeagueID);

    const managementLeagueID = user.memberships
      .filter(
        (membership) =>
          isMembershipCurrentlyActive(membership) &&
          (membership.role === "admin" || membership.role === "leagueManager"),
      )
      .map(resolvedLeagueID)
      .find((leagueID): leagueID is string => !!leagueID);
    if (managementLeagueID) return this.capabilityForLeague(managementLeagueID);

    if (this.context.isSBotLabsAdmin) {
      const optionLeagueID = this.context.workspaceOptions.find((option) => option.leagueID)?.leagueID;
      if (optionLeagueID) return this.capabilityForLeague(optionLeagueID);
    }

    throw new SBotToolAuthorizationError("noActiveLeague");
  }

  capabilityFromLeagueHandle(leagueHandle: string | undefined): SBotToolLeagueCapability {
    if (leagueHandle && leagueHandle.trim().length > 0) {
      const leagueID = parseHandle(leagueHandle.trim(), "league");
      const scopeLeagueID = this.context.workspaceScope?.leagueID?.trim();
      if (scopeLeagueID && scopeLeagueID !== leagueID) {
        throw new SBotToolAuthorizationError("workspaceScopeMismatch");
      }
      return this.capabilityForLeague(leagueID);
    }
    return this.activeLeagueCapability();
  }

  capabilityForLeague(leagueID: string): SBotToolLeagueCapability {
    const user = this.requireUser();
    const isSBotLabsAdmin = this.context.isSBotLabsAdmin;

    const managementMembership = user.memberships.find(
      (membership) =>
        isMembershipCurrentlyActive(membership) &&
        resolvedLeagueID(membership) === leagueID &&
        (membership.role === "admin" || membership.role === "leagueManager"),
    );
    const anyMembership = user.memberships.find(
      (membership) => isMembershipCurrentlyActive(membership) && resolvedLeagueID(membership) === leagueID,
    );

    const isAdmin = isSBotLabsAdmin || managementMembership?.role === "admin";
    const isManager = managementMembership?.role === "leagueManager";
    const canManageData = isSBotLabsAdmin || isAdmin || isManager;

    if (!isSBotLabsAdmin && !isAdmin && !isManager && !anyMembership) {
      throw new SBotToolAuthorizationError("notLeagueMember");
    }

    const membership = managementMembership ?? anyMembership;
    const role = membership?.role ?? user.activeContext?.role;
    const sportID = this.context.workspaceScope?.sportID ?? user.activeContext?.sportID ?? membership?.sportID;
    const leagueName =
      this.context.workspaceScope?.displayName ??
      membership?.displayName ??
      user.activeContext?.displayName ??
      leagueID;

    return {
      leagueID,
      leagueName,
      sportID,
      role,
      isSBotLabsAdmin,
      canManageSettings: isSBotLabsAdmin || isAdmin === true,
      canManageInvitations: isSBotLabsAdmin || isAdmin === true || this.canInviteAsOrganizationAdmin(user, leagueID),
      canManageAthleteData: canManageData,
      canManageCompetitionData: canManageData,
      canManageOrganizationAdminData: isSBotLabsAdmin || isAdmin === true,
      canUseAI: canUseAI(user, isSBotLabsAdmin, leagueID),
      canReadBroadcastOverlays: canReadBroadcastOverlays(user, isSBotLabsAdmin, leagueID),
      canManageBroadcastOverlays: canManageBroadcastOverlays(user, isSBotLabsAdmin, leagueID),
    };
  }

  requireLeagueAdmin(capability: SBotToolLeagueCapability): void {
    if (!capability.canManageSettings) throw new SBotToolAuthorizationError("missingLeagueAdminRole");
  }

  requireLeagueDataManager(capability: SBotToolLeagueCapability): void {
    if (!capability.canManageAthleteData && !capability.canManageCompetitionData) {
      throw new SBotToolAuthorizationError("missingLeagueManagementRole");
    }
  }

  requireBroadcastOverlayRead(capability: SBotToolLeagueCapability): void {
    if (!capability.canUseAI || !capability.canReadBroadcastOverlays) {
      throw new SBotToolAuthorizationError("missingBroadcastOverlayReadAccess");
    }
  }

  requireBroadcastOverlayManagement(capability: SBotToolLeagueCapability): void {
    if (!capability.canUseAI || !capability.canManageBroadcastOverlays) {
      throw new SBotToolAuthorizationError("missingBroadcastOverlayManagementRole");
    }
  }

  requireOrganizationAdminInviteAccess(leagueID: string, organizationID: string | undefined): void {
    const user = this.requireUser();
    if (this.context.isSBotLabsAdmin) return;
    if (
      user.memberships.some(
        (membership) =>
          isMembershipCurrentlyActive(membership) &&
          resolvedLeagueID(membership) === leagueID &&
          membership.role === "admin" &&
          membership.targetType === "league",
      )
    ) {
      return;
    }
    if (
      organizationID &&
      user.memberships.some(
        (membership) =>
          isMembershipCurrentlyActive(membership) &&
          membership.targetType === "organization" &&
          membership.targetID === organizationID &&
          membership.role === "admin" &&
          resolvedLeagueID(membership) === leagueID,
      )
    ) {
      return;
    }
    throw new SBotToolAuthorizationError("missingInviteRole");
  }

  canInviteAsOrganizationAdmin(user: SBotUser, leagueID: string): boolean {
    return user.memberships.some(
      (membership) =>
        isMembershipCurrentlyActive(membership) &&
        membership.targetType === "organization" &&
        membership.role === "admin" &&
        resolvedLeagueID(membership) === leagueID,
    );
  }

  get workspaceScope(): SBotChatWorkspaceScope | undefined {
    return this.context.workspaceScope;
  }

  get isSBotLabsAdmin(): boolean {
    return this.context.isSBotLabsAdmin;
  }

  get currentUser(): SBotUser | null {
    return this.context.currentUser;
  }

  get activeMembershipInfo(): SBotMembership | undefined {
    return this.context.currentUser ? activeMembership(this.context.currentUser) : undefined;
  }
}

/** Mirrors SBotActiveContext.resolvedLeagueID. */
function resolvedContextLeagueID(context: { targetType: string; targetID: string; leagueID?: string | undefined }): string | undefined {
  return context.leagueID ?? (context.targetType === "league" ? context.targetID : undefined);
}

/** capability.snapshot — embedded as `context` in read-tool results. */
export function capabilitySnapshot(capability: SBotToolLeagueCapability): Record<string, ToolJSONValue> {
  return {
    leagueHandle: makeHandle("league", capability.leagueID),
    leagueID: capability.leagueID,
    sportID: capability.sportID ?? null,
    leagueName: capability.leagueName,
    role: capability.role ?? null,
    isSBotLabsAdmin: capability.isSBotLabsAdmin,
    canManageSettings: capability.canManageSettings,
    canManageInvitations: capability.canManageInvitations,
    canManageAthleteData: capability.canManageAthleteData,
    canManageCompetitionData: capability.canManageCompetitionData,
    canManageOrganizationAdminData: capability.canManageOrganizationAdminData,
    canUseAI: capability.canUseAI,
    canReadBroadcastOverlays: capability.canReadBroadcastOverlays,
    canManageBroadcastOverlays: capability.canManageBroadcastOverlays,
  };
}
